import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, photoAPI } from '../../lib/api';
import { Search, Eye, EyeOff, AlertTriangle, Check, Trash2, ChevronLeft, ChevronRight, X, Clock, Link2, ExternalLink } from 'lucide-react';

const LISTING_STATUS_COLORS = {
  active: 'bg-green-100 text-green-700', sold: 'bg-blue-100 text-blue-700',
  hidden: 'bg-yellow-100 text-yellow-700', blocked: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-600', pending: 'bg-amber-100 text-amber-700',
  rejected: 'bg-red-100 text-red-600',
};
const LISTING_STATUS_LABELS = {
  active: 'Active', sold: 'Vendue', hidden: 'Masquée',
  blocked: 'Bloquée', cancelled: 'Annulée', pending: 'En attente',
  rejected: 'Refusée',
};

const ListingsTab = () => {
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [deleting, setDeleting] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [reassigningId, setReassigningId] = useState(null);
  const [reassignEmail, setReassignEmail] = useState('');
  const [reassignSourceUrl, setReassignSourceUrl] = useState('');

  const load = useCallback(() => {
    const params = { page, limit: 20 };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    adminAPI.getListings(params).then(r => { setListings(r.data.listings); setTotal(r.data.total); }).catch(() => {});
  }, [page, search, statusFilter]);
  useEffect(load, [load]);

  const handleDelete = async (listing) => {
    if (!window.confirm(`Supprimer "${listing.brand} ${listing.model}" ?`)) return;
    setDeleting(listing.id);
    try { await adminAPI.deleteListing(listing.id); load(); } catch {} finally { setDeleting(null); }
  };
  const handleStatusChange = async (listingId, newStatus) => {
    try { await adminAPI.updateListingStatus(listingId, newStatus); load(); } catch {}
  };
  const handleApprove = async (listingId) => {
    setActionLoading(listingId);
    try { await adminAPI.approveListing(listingId); load(); } catch {} finally { setActionLoading(null); }
  };
  const handleReject = async (listingId) => {
    if (!rejectReason.trim()) return;
    setActionLoading(listingId);
    try { await adminAPI.rejectListing(listingId, rejectReason); setRejectingId(null); setRejectReason(''); load(); } catch {} finally { setActionLoading(null); }
  };
  const handleReassign = async (listingId) => {
    if (!reassignEmail.trim()) return;
    setActionLoading(listingId);
    try {
      const r = await adminAPI.reassignListing(listingId, reassignEmail.trim(), reassignSourceUrl.trim());
      alert(r.data.message);
      setReassigningId(null); setReassignEmail(''); setReassignSourceUrl(''); load();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); } finally { setActionLoading(null); }
  };

  return (
    <div data-testid="admin-listings">
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8D4E3]" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Rechercher une annonce..." className="w-full h-9 pl-9 pr-3 rounded-lg border border-[#D6E8F0] text-sm" data-testid="admin-listings-search" />
        </div>
        <span className="text-xs text-[#5A8BA8] self-center ml-auto">{total} annonce{total !== 1 ? 's' : ''}</span>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {['', 'pending', 'active', 'sold', 'rejected', 'hidden', 'blocked', 'cancelled'].map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === s ? 'bg-[#0C63BF] text-white' : 'bg-white border border-[#D6E8F0] text-[#5A8BA8] hover:bg-[#F0F7FA]'}`}
            data-testid={`admin-listings-filter-${s || 'all'}`}>
            {s ? (LISTING_STATUS_LABELS[s] || s) : 'Toutes'}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {listings.map(l => (
          <div key={l.id} className={`bg-white rounded-xl border p-3 sm:p-4 ${l.status === 'pending' ? 'border-amber-200 bg-amber-50/30' : 'border-[#E8F4FD]'}`} data-testid={`admin-listing-${l.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {l.photos?.[0] && (
                    <img src={photoAPI.getUrl(l.photos[0])} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-[#033871] truncate">{l.brand} {l.model}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${LISTING_STATUS_COLORS[l.status] || 'bg-gray-100'}`}>
                        {LISTING_STATUS_LABELS[l.status] || l.status}
                      </span>
                    </div>
                    <p className="text-[11px] text-[#5A8BA8] mt-0.5">Vendeur : {l.seller_full_name} ({l.seller_email})</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-[#5A8BA8]">
                  <span className="font-bold text-[#0C63BF]">{l.price?.toFixed(2)}&euro;</span>
                  <span>Etat : {l.condition_score}/10</span>
                  <span><Eye className="w-3 h-3 inline" /> {l.views || 0}</span>
                  {l.created_at && <span className="text-[10px]">{new Date(l.created_at).toLocaleDateString('fr-FR')}</span>}
                </div>
                {l.description && <p className="text-xs text-[#5A8BA8] mt-1 line-clamp-2">{l.description}</p>}
                {l.status === 'rejected' && l.rejection_reason && (
                  <p className="text-xs text-red-600 mt-1">Motif : {l.rejection_reason}</p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {l.status === 'pending' && (
                  <>
                    <button onClick={() => handleApprove(l.id)} disabled={actionLoading === l.id}
                      className="px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-medium hover:bg-green-600 disabled:opacity-50 flex items-center gap-1"
                      title="Approuver" data-testid={`approve-listing-${l.id}`}>
                      <Check className="w-3.5 h-3.5" /> Approuver
                    </button>
                    <button onClick={() => { setRejectingId(l.id); setRejectReason(''); }}
                      className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 flex items-center gap-1"
                      title="Refuser" data-testid={`reject-listing-${l.id}`}>
                      <X className="w-3.5 h-3.5" /> Refuser
                    </button>
                  </>
                )}
                {l.status === 'active' && <button onClick={() => handleStatusChange(l.id, 'hidden')} className="p-1.5 rounded-lg bg-yellow-50 text-yellow-600 hover:bg-yellow-100" title="Masquer"><EyeOff className="w-4 h-4" /></button>}
                {l.status === 'hidden' && <button onClick={() => handleStatusChange(l.id, 'active')} className="p-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100" title="Réactiver"><Eye className="w-4 h-4" /></button>}
                {l.status === 'blocked' && <button onClick={() => handleStatusChange(l.id, 'active')} className="p-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100" title="Débloquer"><Check className="w-4 h-4" /></button>}
                {!['blocked', 'cancelled', 'sold', 'pending'].includes(l.status) && <button onClick={() => handleStatusChange(l.id, 'blocked')} className="p-1.5 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100" title="Bloquer"><AlertTriangle className="w-4 h-4" /></button>}
                <button onClick={() => handleDelete(l)} disabled={deleting === l.id} className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-50" title="Supprimer"><Trash2 className={`w-4 h-4 ${deleting === l.id ? 'animate-spin' : ''}`} /></button>
                <button onClick={() => { setReassigningId(reassigningId === l.id ? null : l.id); setReassignEmail(''); setReassignSourceUrl(l.source_url || ''); }}
                  className="px-2 py-1.5 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 text-[10px] font-medium" title="Réattribuer"
                  data-testid={`reassign-listing-${l.id}`}>
                  Réattribuer
                </button>
              </div>
              {/* Source URL indicator for admin */}
              {l.source_url && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-purple-600 bg-purple-50 rounded-lg px-2 py-1">
                  <Link2 className="w-3 h-3" />
                  <a href={l.source_url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-[200px]">
                    {l.source_url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                  </a>
                  <a href={l.source_url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
            {/* Reject form */}
            {rejectingId === l.id && (
              <div className="mt-3 pt-3 border-t border-red-100 space-y-2" data-testid={`reject-form-${l.id}`}>
                <label className="text-xs font-semibold text-red-600">Motif du refus :</label>
                <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                  placeholder="Ex: Photos floues, description incomplète, prix incohérent..."
                  className="w-full h-20 px-3 py-2 rounded-lg border border-red-200 text-sm resize-none focus:border-red-400 outline-none"
                  data-testid={`reject-reason-input-${l.id}`} />
                <div className="flex gap-2">
                  <button onClick={() => handleReject(l.id)} disabled={!rejectReason.trim() || actionLoading === l.id}
                    className="px-4 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 disabled:opacity-50"
                    data-testid={`confirm-reject-${l.id}`}>
                    Confirmer le refus
                  </button>
                  <button onClick={() => setRejectingId(null)} className="px-4 py-1.5 rounded-lg border border-[#D6E8F0] text-xs text-[#5A8BA8]">Annuler</button>
                </div>
              </div>
            )}
            {/* Reassign form */}
            {reassigningId === l.id && (
              <div className="mt-3 pt-3 border-t border-purple-100 space-y-2" data-testid={`reassign-form-${l.id}`}>
                <label className="text-xs font-semibold text-purple-600">Email du propriétaire :</label>
                <input type="email" value={reassignEmail} onChange={e => setReassignEmail(e.target.value)}
                  placeholder="vendeur@email.com"
                  className="w-full h-9 px-3 rounded-lg border border-purple-200 text-sm focus:border-purple-400 outline-none"
                  data-testid={`reassign-email-input-${l.id}`} />
                <label className="text-xs font-semibold text-purple-600 flex items-center gap-1">
                  <Link2 className="w-3 h-3" /> URL source (admin uniquement) :
                </label>
                <input type="url" value={reassignSourceUrl} onChange={e => setReassignSourceUrl(e.target.value)}
                  placeholder="https://www.leboncoin.fr/..."
                  className="w-full h-9 px-3 rounded-lg border border-purple-200 text-sm focus:border-purple-400 outline-none"
                  data-testid={`reassign-url-input-${l.id}`} />
                <div className="flex gap-2">
                  <button onClick={() => handleReassign(l.id)} disabled={!reassignEmail.trim() || actionLoading === l.id}
                    className="px-4 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-medium hover:bg-purple-600 disabled:opacity-50"
                    data-testid={`confirm-reassign-${l.id}`}>
                    Transférer l'annonce
                  </button>
                  <button onClick={() => setReassigningId(null)} className="px-4 py-1.5 rounded-lg border border-[#D6E8F0] text-xs text-[#5A8BA8]">Annuler</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {listings.length === 0 && <p className="text-center text-sm text-[#5A8BA8] py-8">Aucune annonce</p>}
      </div>
      {total > 20 && (
        <div className="flex justify-center items-center gap-3 mt-4">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg border disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-[#5A8BA8]">Page {page}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={listings.length < 20} className="p-1.5 rounded-lg border disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
};

export default ListingsTab;
